import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import StockOperations from "./pages/StockOperations";
import Transfer from "./pages/Transfer";
import Recipes from "./pages/Recipes";
import Sales from "./pages/Sales";
import Reports from "./pages/Reports";
import Suppliers from "./pages/Suppliers";
import UserManagement from "./pages/UserManagement";
import InventoryCount from "./pages/InventoryCount";
import SmartMenu from "./pages/SmartMenu";
import TransferRequests from "./pages/TransferRequests";
import AutoOrderSuggestions from "./pages/AutoOrderSuggestions";
import StaffMeals from "./pages/StaffMeals";
import MealPlanning from "./pages/MealPlanning";
import Staff from "./pages/Staff";
import Expenses from "./pages/Expenses";
import Processing from "./pages/Processing";
import StockReport from "./pages/StockReport";
import TransferCostReport from "./pages/TransferCostReport";
import PaymentReport from "./pages/PaymentReport";
import CheckManagement from "./pages/CheckManagement";
import LoanManagement from "./pages/LoanManagement";
import VegetableManagement from "./pages/VegetableManagement";
import ProfitLoss from "./pages/ProfitLoss";
import MeatManagement from "./pages/MeatManagement";
import GeneralProducts from "./pages/GeneralProducts";
import MonthlyReport from "./pages/MonthlyReport";
import CashRegister from "./pages/CashRegister";
import WasteTracking from "./pages/WasteTracking";
import UserPermissions from "./pages/UserPermissions";
import SmartAnalytics from "./pages/SmartAnalytics";
import Gomsu360 from "./pages/Gomsu360";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading, serverWaking } = useAuth();

  if (loading || serverWaking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        {serverWaking && (
          <p className="text-muted-foreground text-sm animate-pulse">
            Sunucu başlatılıyor, lütfen bekleyin...
          </p>
        )}
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route Component (redirect if logged in)
function PublicRoute({ children }) {
  const { user, loading, serverWaking } = useAuth();

  if (loading || serverWaking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        {serverWaking && (
          <p className="text-muted-foreground text-sm animate-pulse">
            Sunucu başlatılıyor, lütfen bekleyin...
          </p>
        )}
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Admin Only Route Component
function AdminRoute({ children }) {
  const { user, loading, serverWaking } = useAuth();

  if (loading || serverWaking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Products />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock"
        element={
          <ProtectedRoute>
            <StockOperations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer"
        element={
          <ProtectedRoute>
            <Transfer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes"
        element={
          <ProtectedRoute>
            <Recipes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <Sales />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer-cost-report"
        element={
          <ProtectedRoute>
            <TransferCostReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-report"
        element={
          <ProtectedRoute>
            <PaymentReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute>
            <Suppliers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checks"
        element={
          <ProtectedRoute>
            <CheckManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/loans"
        element={
          <ProtectedRoute>
            <LoanManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vegetables"
        element={
          <ProtectedRoute>
            <VegetableManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profit-loss"
        element={
          <ProtectedRoute>
            <ProfitLoss />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monthly-report"
        element={
          <ProtectedRoute>
            <MonthlyReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meat"
        element={
          <ProtectedRoute>
            <MeatManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/general-products"
        element={
          <ProtectedRoute>
            <GeneralProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory-count"
        element={
          <ProtectedRoute>
            <InventoryCount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/smart-menu"
        element={
          <ProtectedRoute>
            <SmartMenu />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer-requests"
        element={
          <ProtectedRoute>
            <TransferRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/auto-orders"
        element={
          <ProtectedRoute>
            <AutoOrderSuggestions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-meals"
        element={
          <ProtectedRoute>
            <StaffMeals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meal-planning"
        element={
          <ProtectedRoute>
            <MealPlanning />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff"
        element={
          <ProtectedRoute>
            <Staff />
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <Expenses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/processing"
        element={
          <ProtectedRoute>
            <Processing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock-report"
        element={
          <ProtectedRoute>
            <StockReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cash-register"
        element={
          <ProtectedRoute>
            <CashRegister />
          </ProtectedRoute>
        }
      />
      <Route
        path="/waste-tracking"
        element={
          <ProtectedRoute>
            <WasteTracking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/permissions"
        element={
          <AdminRoute>
            <UserPermissions />
          </AdminRoute>
        }
      />

      <Route
        path="/smart-analytics"
        element={
          <ProtectedRoute>
            <SmartAnalytics />
          </ProtectedRoute>
        }
      />

      <Route
        path="/gomsu-360"
        element={
          <ProtectedRoute>
            <Gomsu360 />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
    <LanguageProvider>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              color: 'hsl(var(--foreground))',
            },
          }}
        />
      </AuthProvider>
    </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
